/**
 * BatchAIModal - Modal Geracao em Lote (MASSA) - extraído VERBATIM de ProductsContent.js (Fase 2 refactor).
 * Recebe estado/handlers via props do closure de ProductsContent.
 */
import React from 'react';
import { Image as ImageIcon, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const BatchAIModal = ({
  showBatchAIModal,
  setShowBatchAIModal,
  batchLoading,
  batchProducts,
  batchProgress,
  batchResults,
  startBatchGeneration,
  cancelBatchGeneration,
  aiImageConfig,
  setAiImageConfig,
  categories,
  loading,
}) => {
  return (
      <Dialog open={showBatchAIModal} onOpenChange={(open) => !batchLoading && setShowBatchAIModal(open)}>
        <DialogContent className="glass-modal max-w-2xl max-h-[95vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b border-gray-700/50 flex-shrink-0">
            <DialogTitle className="text-white text-lg flex items-center gap-2">
              <Sparkles size={18} className="text-purple-400" />
              Gerar Imagens em Massa
            </DialogTitle>
            <p className="text-sm text-gray-400 mt-1">
              {batchProducts.length} produtos serão processados automaticamente
            </p>
          </DialogHeader>
          
          {/* Área com scroll */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {batchProducts.length === 0 ? (
              <div className="text-center py-8">
                <ImageIcon size={48} className="mx-auto text-gray-600 mb-3" />
                <p className="text-gray-400">Todos os produtos já têm imagem!</p>
              </div>
            ) : batchProgress.status === 'idle' ? (
              <>
                {/* Configurações */}
                <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
                  <h3 className="text-white font-medium text-sm">Configurações para todas as imagens:</h3>
                  
                  {/* Chave Emergent */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">🔑 Chave Emergent Universal</label>
                    <input
                      type="password"
                      className="glass-input w-full text-sm py-2 px-3"
                      value={aiImageConfig.emergent_key}
                      onChange={(e) => setAiImageConfig({...aiImageConfig, emergent_key: e.target.value})}
                      placeholder="sk-emergent-..."
                    />
                  </div>
                  
                  {/* Estilo e Ambiente */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">📷 Estilo</label>
                      <select
                        className="glass-input w-full text-sm py-2 px-2"
                        value={aiImageConfig.photo_style}
                        onChange={(e) => setAiImageConfig({...aiImageConfig, photo_style: e.target.value})}
                      >
                        <option value="amateur">📱 Caseira/Amadora</option>
                        <option value="delivery">📦 Delivery Real</option>
                        <option value="semi_pro">📸 Semi-profissional</option>
                        <option value="professional">🎬 Profissional</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">🇧🇷 Ambiente Brasileiro</label>
                      <select
                        className="glass-input w-full text-sm py-2 px-2"
                        value={aiImageConfig.environment}
                        onChange={(e) => setAiImageConfig({...aiImageConfig, environment: e.target.value})}
                      >
                        <option value="auto">🎯 Auto-detectar</option>
                        <optgroup label="🇧🇷 Ambientes Brasileiros">
                          <option value="mesa_casa_br">🏠 Mesa de casa BR</option>
                          <option value="balcao_lanchonete">🍔 Lanchonete BR</option>
                          <option value="self_service">🍛 Self-service/Quilo</option>
                          <option value="marmitex">📦 Marmitex</option>
                          <option value="churrascaria">🥩 Churrascaria</option>
                          <option value="feira_rua">🛒 Feira/Rua</option>
                          <option value="acaiteria">🍇 Açaiteria</option>
                          <option value="boteco">🍺 Boteco</option>
                          <option value="pizzaria_br">🍕 Pizzaria BR</option>
                          <option value="padaria_br">🥐 Padaria BR</option>
                          <option value="pastelaria">🥟 Pastelaria</option>
                        </optgroup>
                        <optgroup label="⬜ Neutros">
                          <option value="prato_simples">🍽️ Prato simples</option>
                          <option value="tabua_madeira">🪵 Tábua de madeira</option>
                          <option value="embalagem_delivery">📦 Embalagem delivery</option>
                          <option value="fundo_neutro">⬜ Fundo neutro</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {/* Detalhes extras (aplicado a TODAS as imagens) */}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">💬 Detalhes extras (para todas)</label>
                    <textarea
                      className="glass-input w-full text-sm py-2 px-3 resize-none"
                      rows={2}
                      value={aiImageConfig.extra_details}
                      onChange={(e) => setAiImageConfig({...aiImageConfig, extra_details: e.target.value})}
                      placeholder='Ex: embalagem com o nome "Burger do Zé" impresso, guardanapo da marca...'
                      data-testid="bulk-ai-extra-details-input"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">Aplicado a todos os produtos. Ex: nome da loja na embalagem, cores da marca.</p>
                  </div>

                  {/* Usar logomarca da loja (todas) */}
                  <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer bg-slate-800/40 border border-white/10 rounded-lg p-2.5">
                    <input
                      type="checkbox"
                      checked={aiImageConfig.use_logo}
                      onChange={(e) => setAiImageConfig({...aiImageConfig, use_logo: e.target.checked})}
                      className="rounded border-gray-600 w-4 h-4 mt-0.5"
                      data-testid="bulk-ai-use-logo-checkbox"
                    />
                    <span>🏷️ Usar minha logomarca em todas
                      <span className="block text-[11px] text-gray-500">A IA estampa a logo da loja na embalagem/cenário. Cadastre a logo em Configurações.</span>
                    </span>
                  </label>

                  <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiImageConfig.remember_config}
                      onChange={(e) => setAiImageConfig({...aiImageConfig, remember_config: e.target.checked})}
                      className="rounded border-gray-600 w-4 h-4"
                    />
                    Lembrar chave
                  </label>
                </div>
                
                {/* Lista de produtos que serão processados */}
                <div>
                  <h3 className="text-white font-medium text-sm mb-2">Produtos a processar ({batchProducts.length}):</h3>
                  <div className="bg-gray-800/30 rounded-lg max-h-48 overflow-y-auto">
                    {batchProducts.map((p, i) => (
                      <div key={p.id} className="flex items-center gap-2 px-3 py-2 border-b border-gray-700/30 last:border-0">
                        <span className="text-xs text-gray-500 w-6">{i + 1}.</span>
                        <span className="text-sm text-gray-300 flex-1 truncate">{p.name}</span>
                        <span className="text-xs text-gray-500">{categories.find(c => c.id === p.category_id)?.name || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Progresso da geração */}
                <div className="space-y-4">
                  {/* Barra de progresso */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-300">
                        {batchProgress.status === 'running' ? 'Gerando imagens...' : '✅ Concluído!'}
                      </span>
                      <span className="text-sm text-white font-medium">
                        {batchProgress.current} / {batchProgress.total}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                      />
                    </div>
                    {batchProgress.status === 'running' && (
                      <p className="text-xs text-gray-500 mt-2">
                        ⏳ Processando: {batchProducts[batchProgress.current - 1]?.name || '...'}
                      </p>
                    )}
                  </div>
                  
                  {/* Resultados em tempo real */}
                  <div>
                    <h3 className="text-white font-medium text-sm mb-2">Resultados:</h3>
                    <div className="bg-gray-800/30 rounded-lg max-h-64 overflow-y-auto">
                      {batchResults.map((r, i) => (
                        <div key={r.productId} className="flex items-center gap-2 px-3 py-2 border-b border-gray-700/30 last:border-0">
                          <span className={`text-lg ${r.success ? 'text-green-400' : 'text-red-400'}`}>
                            {r.success ? '✓' : '✗'}
                          </span>
                          <span className="text-sm text-gray-300 flex-1 truncate">{r.name}</span>
                          {r.success ? (
                            <img src={r.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <span className="text-xs text-red-400 truncate max-w-32">{r.error}</span>
                          )}
                        </div>
                      ))}
                      {batchProgress.status === 'running' && batchResults.length < batchProgress.total && (
                        <div className="flex items-center gap-2 px-3 py-2">
                          <div className="loading-spinner" style={{width: '16px', height: '16px'}}></div>
                          <span className="text-sm text-gray-500">Aguardando...</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Resumo final */}
                  {batchProgress.status === 'done' && (
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                      <p className="text-green-400 font-medium">
                        🎉 {batchResults.filter(r => r.success).length} de {batchResults.length} imagens geradas com sucesso!
                      </p>
                      {batchResults.filter(r => !r.success).length > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          {batchResults.filter(r => !r.success).length} produto(s) falharam - você pode tentar novamente depois
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Botões fixos */}
          <div className="p-4 pt-2 border-t border-gray-700/50 flex-shrink-0 bg-gray-900/80">
            {batchProducts.length === 0 ? (
              <button
                type="button"
                onClick={() => setShowBatchAIModal(false)}
                className="w-full glass-button py-3 text-sm"
              >
                Fechar
              </button>
            ) : batchProgress.status === 'idle' ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowBatchAIModal(false)}
                  className="flex-1 glass-button py-3 text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={startBatchGeneration}
                  disabled={!aiImageConfig.emergent_key}
                  className="flex-1 glass-button-primary py-3 text-sm disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                >
                  <Sparkles size={16} />
                  Gerar {batchProducts.length} Imagens
                </button>
              </div>
            ) : batchProgress.status === 'running' ? (
              <button
                type="button"
                disabled
                className="w-full glass-button py-3 text-sm opacity-50 flex items-center justify-center gap-2"
              >
                <div className="loading-spinner" style={{width: '16px', height: '16px'}}></div>
                Processando... ({batchProgress.current}/{batchProgress.total})
              </button>
            ) : (
              <button
                type="button"
                onClick={cancelBatchGeneration}
                className="w-full glass-button-primary py-3 text-sm font-medium"
              >
                ✅ Concluir
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

  );
};

export default BatchAIModal;
